import React, { useState } from "react";
import {
  Formik,
  Field,
  Form,
  FormikHelpers,
  FormikProps,
  useFormikContext,
} from "formik";
import * as Yup from "yup";
import { Button, TextField, Grid, Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useHandleError } from "../../hooks/useHandleError";
import SignInLayout from "../../layouts/SignInLayout";
import { enqueueSnackbar } from "notistack";

interface SignInProps {
  // navigateToSignUp?: () => void;
  // navigateToReset?: (email: string) => void;
  // navigateToEmailConfirm?: (email: string) => void;
}

interface FormValues {
  email: string;
  password: string;
  newPassword?: string;
  confirmPassword?: string;
  newPasswordUser?: boolean;
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  password: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  newPasswordUser: Yup.boolean(),
  newPassword: Yup.string().when("newPasswordUser", {
    is: true,
    then: (schema) =>
      schema
        .required("New password is required")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
          "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number."
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  confirmPassword: Yup.string().when("newPasswordUser", {
    is: true,
    then: (schema) =>
      schema
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const SignIn: React.FC<SignInProps> = () => {
  const auth = useAuth();

  const handleError = useHandleError();

  const navigate = useNavigate();
  const [newPasswordUser, setNewPasswordUser] = useState<any | null>(null);

  const initialValues: FormValues = {
    email: "",
    password: "",
    newPasswordUser: false,
  };

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      if (newPasswordUser) {
        await auth.completeNewPassword(newPasswordUser, values.newPassword!);
        setNewPasswordUser(null);
      } else {
        const result = await auth.signIn(values.email, values.password);
        if (result?.challengeType === "NEW_PASSWORD_REQUIRED") {
          setNewPasswordUser(result.user); // Pass the actual CognitoUser
        }
      }
    } catch (error: any) {
      if (
        error?.code == "UserNotConfirmedException" ||
        error?.message === "User is not confirmed."
      ) {
        // navigateToEmailConfirm
        //   ? navigateToEmailConfirm(values.email)
        //   : navigate("/email-confirmation", { state: { email: values.email } });

        navigate("/auth/email-confirmation", {
          state: { email: values.email },
        });
        enqueueSnackbar(`Error signing in: Email is not confirmed.`, {
          variant: "error",
        });
      } else {
      }
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SignInLayout>
      <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
        {newPasswordUser ? "Set New Password" : "Sign in"}
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          handleChange,
          handleBlur,
          values,
          errors,
          touched,
        }: FormikProps<FormValues>) => (
          <Form noValidate>
            <Grid container spacing={2}>
              {!newPasswordUser && (
                <>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      margin="normal"
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      helperText={touched.email ? errors.email : ""}
                      error={touched.email && Boolean(errors.email)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      helperText={touched.password ? errors.password : ""}
                      error={touched.password && Boolean(errors.password)}
                    />
                  </Grid>
                </>
              )}

              {newPasswordUser && (
                <>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      margin="normal"
                      required
                      fullWidth
                      name="newPassword"
                      label="New Password"
                      type="password"
                      id="newPassword"
                      autoComplete="new-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.newPassword}
                      helperText={touched.newPassword ? errors.newPassword : ""}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      margin="normal"
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password"
                      type="password"
                      id="confirmPassword"
                      autoComplete="confirm-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.confirmPassword}
                      helperText={
                        touched.confirmPassword ? errors.confirmPassword : ""
                      }
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{ mt: 3, mb: 2 }}
            >
              {newPasswordUser ? "Set New Password" : "Sign In"}
            </Button>

            {!newPasswordUser && (
              <Grid container justifyContent="space-between" sx={{ mt: 1 }}>
                <Grid item>
                  <Link
                    href="#"
                    variant="body2"
                    onClick={() => navigate("/auth/password-reset")}
                  >
                    {"Forgot password"}
                  </Link>
                </Grid>
                <Grid item>
                  <Link
                    href="#"
                    variant="body2"
                    onClick={() => navigate("/auth/sign-up")}
                  >
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            )}
          </Form>
        )}
      </Formik>
    </SignInLayout>
  );
};

export default SignIn;
